import React, { useState } from "react";
import { Control, Controller, RegisterOptions } from "react-hook-form";
import {
  Button as AriaButton,
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  DateInput,
  DatePicker,
  DateSegment,
  Dialog,
  Group,
  Heading,
  Label as AriaLabel,
  OverlayArrow,
  Popover,
  DateValue,
} from "react-aria-components";
import { Label } from "semantic-ui-react";
import { parseAbsolute, toZoned } from "@internationalized/date";
import { useTimezone } from "../../../hooks/useTimezone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt } from "@fortawesome/pro-regular-svg-icons";
import { faAngleLeft, faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { Segment, Button } from "semantic-ui-react";
import classNames from "classnames";
import { ZonedDateTime } from "../ZonedDateTime";
import { fieldError } from "../../../utils/functions";

type Props = {
  control: Control;
  name: string;
  label?: string;
  granularity?: "day" | "hour" | "minute" | "second";
  timeWithRespectToAgencyTimezone?: boolean;
  readonly?: boolean;
  errors?: any;
  required?: boolean;
  rules?: Exclude<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
  isDateUnavailable?: (date: DateValue) => boolean;
  isInvalid?: boolean;
  UNSTABLE_portalContainer?: Element | undefined;
};

export const ControlledDateTime = (props: Props) => {
  const {
    control,
    name,
    label,
    granularity = "minute",
    timeWithRespectToAgencyTimezone = true,
    readonly = false,
    errors,
    required = false,
    rules,
  } = props;

  const error = fieldError(errors, name);

  const [focused, setFocused] = useState(false);
  const timezoneInfo = useTimezone({ defaultToAgencyTimezone: timeWithRespectToAgencyTimezone });

  const timezone = timezoneInfo.defaultTimezoneInfo.timezone;

  const shouldHideTimezone = !timeWithRespectToAgencyTimezone || timezoneInfo.clientIsInAgencyTimezone;

  return (
    <Controller
      name={name}
      control={control}
      rules={rules ?? {}}
      render={({ onChange, value }) => {
        const zonedDate = value ? parseAbsolute(new Date(value).toISOString(), timezone) : null;

        if (readonly) {
          return (
            <div className='tw-flex tw-flex-col'>
              <label className='tw-font-bold'>{label}</label>
              <ZonedDateTime value={zonedDate?.toDate()} />
            </div>
          );
        }

        return (
          <DatePicker
            className='tw-flex tw-flex-col'
            value={zonedDate}
            onChange={(value) => {
              if (value) {
                onChange(toZoned(value, timezone).toDate());
              }
            }}
            onFocusChange={(isFocused) => {
              setFocused(isFocused);
            }}
            hideTimeZone={shouldHideTimezone}
            granularity={granularity}
          >
            {label && (
              <AriaLabel className='label-text tw-mb-1'>
                {label}
                {required && <span className='tw-ml-1 tw-text-red'>*</span>}
              </AriaLabel>
            )}
            <Group
              className={classNames({
                "tw-flex tw-justify-between tw-gap-1 tw-rounded-[.285rem] tw-border-[1px] tw-border-solid tw-px-4 tw-py-[0.53rem] tw-transition-shadow tw-duration-75":
                  true,
                "tw-border-[rgba(34,36,38,.15)]": !focused,
                "tw-border-[#85b7d9]": focused,
                "tw-border-[#E0B4B4] tw-bg-[#FFF6F6]": !!error,
              })}
            >
              <DateInput className='tw-flex tw-h-6 tw-min-w-[150px] tw-items-center'>
                {(segment) => {
                  if (segment.type === "literal" && segment.text === ", ")
                    return <div className='tw-pr-1'>{segment.text}</div>;
                  if (segment.type === "timeZoneName") return <div className='tw-pl-1'>{segment.text}</div>;
                  return <DateSegment segment={segment} />;
                }}
              </DateInput>
              <AriaButton className='tw-border-none tw-bg-transparent tw-p-0'>
                <Button
                  as='div'
                  className='tw-mr-0 tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-bg-transparent tw-p-0 tw-text-grey hover:tw-text-black'
                  icon
                >
                  <FontAwesomeIcon className='tw-text-xl tw-font-bold' icon={faCalendarAlt} />
                </Button>
              </AriaButton>
            </Group>
            {error && (
              <Label basic prompt pointing='above'>
                {error?.content.message}
              </Label>
            )}
            <Popover UNSTABLE_portalContainer={props.UNSTABLE_portalContainer} placement='bottom end'>
              <OverlayArrow></OverlayArrow>
              <Segment className='tw-m-0 tw-shadow-md'>
                <Dialog>
                  <Calendar
                    isDateUnavailable={props.isDateUnavailable}
                    className={"tw-flex tw-flex-col tw-items-center"}
                  >
                    <div className={"tw-mb-1 tw-flex tw-w-full tw-items-center tw-justify-between tw-px-1"}>
                      <AriaButton slot='previous' className='tw-m-0 tw-border-none tw-bg-transparent tw-p-0'>
                        <Button
                          as='div'
                          className='button-ghost tw-mr-0 tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center'
                          icon
                          size='tiny'
                          tabIndex={-1}
                        >
                          <FontAwesomeIcon className='tw-text-xl tw-font-bold tw-text-grey' icon={faAngleLeft} />
                        </Button>
                      </AriaButton>
                      <Heading className='tw-m-0 tw-text-xl' />
                      <AriaButton slot='next' className='tw-m-0 tw-border-none tw-bg-transparent tw-p-0'>
                        <Button
                          as='div'
                          className='button-ghost tw-mr-0 tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center'
                          icon
                          size='tiny'
                          tabIndex={-1}
                        >
                          <FontAwesomeIcon className='tw-text-xl tw-font-bold tw-text-grey' icon={faAngleRight} />
                        </Button>
                      </AriaButton>
                    </div>
                    <CalendarGrid>
                      <CalendarGridHeader className='tw-h-6'>
                        {(day) => <CalendarHeaderCell className='tw-text-xs tw-text-grey'>{day}</CalendarHeaderCell>}
                      </CalendarGridHeader>
                      <CalendarGridBody>
                        {(date) => {
                          return (
                            <CalendarCell
                              className={({ isUnavailable, isDisabled, isSelected, isOutsideMonth }) => {
                                const base =
                                  "tw-forced-color-adjust-none tw-flex tw-h-8 tw-w-8 tw-cursor-default tw-items-center tw-justify-center tw-rounded-md tw-text-sm";
                                if (isDisabled || isUnavailable) return `${base} tw-text-grey`;
                                if (isSelected) return `${base} tw-bg-blue tw-text-white`;
                                if (!isOutsideMonth) return `${base} hover:tw-cursor-pointer hover:tw-bg-grey-light`;
                                return `${base} tw-text-black`;
                              }}
                              date={date}
                            />
                          );
                        }}
                      </CalendarGridBody>
                    </CalendarGrid>
                  </Calendar>
                </Dialog>
              </Segment>
            </Popover>
          </DatePicker>
        );
      }}
    />
  );
};
